import React from 'react'
import {
  Bullet,
  Bullets,
  Info,
  Page,
  Screenshot,
  Section,
  Step,
  Warning,
} from '../../components/DocsComponents'

const ImagingRequests = () => (
  <Page
    title="Imaging Requests"
    intro="Clinicians can request imaging studies such as X-ray and ultrasound from an ePCR, route them through a named approver, attach images of the scan, record a clinical impression, and — for ionising modalities — track the radiation dose against the member of staff who performed the study."
  >
    <Section title="Enabling imaging for your organisation">
      <p>
        Imaging is switched off until an administrator enables it. Everything
        is configured in one place:
      </p>
      <Step number={1} title="Open the imaging settings">
        <p>
          Go to <strong>Site Configuration → Patient Records</strong> and find
          the <strong>Imaging Studies</strong> section, then tick{' '}
          <strong>Enable imaging requests in the ePCR</strong>. The Imaging
          button appears on the ePCR Assess step for everyone once this is
          saved.
        </p>
      </Step>
      <Step number={2} title="Choose your modalities">
        <p>
          X-ray and Ultrasound are provided by default, and you can add,
          rename, or remove modalities to match the equipment you carry. Each
          modality has an <strong>Ionising</strong> flag — ionising modalities
          (such as X-ray) require a pregnancy status before a study can be
          approved, and are the only ones that can have a radiation dose
          recorded.
        </p>
      </Step>
      <Step number={3} title="Pick your imaging approvers">
        <p>
          Choose the named staff who may approve or reject imaging requests.
          Approvers do not need to be in any particular team — the list itself
          is the permission. Changes to the settings are recorded in the
          organisation log.
        </p>
      </Step>
      <Step number={4} title="Decide whether approval is required">
        <p>
          <strong>Require approval before a study can be performed</strong> is
          on by default: requests wait for an approver before images and an
          impression can be recorded. Turning it off approves every request
          automatically at the point it is made.
        </p>
      </Step>
      <Warning title="Regulatory considerations for X-ray">
        Diagnostic imaging with ionising radiation carries its own regulatory
        obligations (IR(ME)R justification roles, and it can affect your CQC
        regulated-activity position). The approval flow and dose records
        support that governance, but your organisation remains responsible
        for its registrations and local rules.
      </Warning>
    </Section>

    <Section title="Requesting a study from an ePCR">
      <p>
        On the Assess step of a full ePCR, select <strong>Imaging</strong> and
        then <strong>New Imaging Request</strong>. A request records the
        modality, the body location (with laterality), a clinical indication,
        and — for ionising modalities — the patient's pregnancy status.
      </p>
      <Bullets>
        <Bullet>
          Each request also appears as an entry on the assessment list, so
          the study forms part of the clinical narrative and appears on the
          record view and PDF alongside the other assessment modules.
        </Bullet>
        <Bullet>
          The entry shows a <strong>Cancel Request</strong> button instead of
          the usual delete icon — cancelling withdraws the request entirely,
          including from the approvers' worklist. Completed studies cannot be
          removed.
        </Bullet>
      </Bullets>
      <Info title="New records need their first save">
        Requests are linked to the saved record, so on a brand-new ePCR the
        Imaging panel becomes available after the first autosave — a few
        seconds after you start the record.
      </Info>
    </Section>

    <Section title="Approving and rejecting requests">
      <Bullets>
        <Bullet>
          When a request is raised, every configured approver receives an app
          notification, and a pulsing <strong>Imaging Approvals</strong> card
          appears first in the Team Actions panel on their dashboard.
        </Bullet>
        <Bullet>
          Approvers work from <strong>Patient Records → Imaging Requests</strong>.
          The page is only accessible to configured approvers, and opens on
          four stat tiles — <strong>Awaiting approval</strong> (with the age
          of the oldest request), <strong>Awaiting study</strong> (approved
          but no impression yet), <strong>Completed this month</strong>, and{' '}
          <strong>Rejected this month</strong>.
        </Bullet>
        <Bullet>
          The <strong>To review</strong> tab shows each waiting request as a
          card; the <strong>History</strong> tab lists everything already
          decided, with a free-text search and status filters. An{' '}
          <strong>Imaging settings</strong> button jumps straight to the
          configuration in Site Configuration.
        </Bullet>
        <Bullet>
          Opening a request — from either tab — uses the review drawer: the
          request details, the pregnancy status, a decision notes box, and
          the Approve / Reject actions. A rejection always requires a reason,
          and the drawer blocks approval of an ionising study until a
          pregnancy status has been recorded on the request. The requesting
          clinician is notified of the outcome either way.
        </Bullet>
        <Bullet>
          Requests can also be reviewed inside the ePCR itself — an approver
          opening the record's Imaging panel sees the same Review action.
        </Bullet>
      </Bullets>
      <Screenshot url="patient-records/imaging-worklist.png">
        The Imaging Requests worklist with its stat tiles and waiting requests
        shown as cards on the To review tab.
      </Screenshot>
    </Section>

    <Section title="Images, the clinical impression, and who performed it">
      <p>
        Once a study is approved, the clinician performs it and records the
        outcome. The assessment-list entry gains a{' '}
        <strong>Record Impression</strong> button that opens everything in
        one dialog:
      </p>
      <Bullets>
        <Bullet>
          <strong>Scan images</strong> — upload photos or exports of the
          scan; they are stored with the record like other attachments.
        </Bullet>
        <Bullet>
          <strong>Clinical impression</strong> — the clinician's
          interpretation. Saving it marks the study complete and writes the
          impression into the assessment entry on the record.
        </Bullet>
        <Bullet>
          <strong>Performed by</strong> — who operated the device, defaulting
          to the logged-in clinician. This drives per-employee dose tracking.
        </Bullet>
        <Bullet>
          <strong>Radiation dose</strong> (ionising modalities only) — the
          reading from the device's post-exposure display, typically a
          dose–area product (DAP) in mGy·cm² or µGy·m², plus the number of
          exposures and the device or unit used.
        </Bullet>
      </Bullets>
      <Info title="After the record is submitted">
        Because imaging requests live alongside the record rather than inside
        it, images and the impression can still be recorded from the
        read-only record view after the ePCR has been submitted.
      </Info>
    </Section>

    <Section title="Radiation dose tracking">
      <Bullets>
        <Bullet>
          Every ionising study's dose is attributed to the member of staff
          recorded as performing it. Totals are always kept per unit —
          readings in different units are never added together.
        </Bullet>
        <Bullet>
          Each employee's page gains a <strong>Radiation Doses</strong> tab
          (visible while imaging is enabled) with study counts, dose totals
          for the last 12 months and all time, and the full study history.
        </Bullet>
        <Bullet>
          The workforce dashboard shows a <strong>Radiation Doses</strong>{' '}
          overview — one row per member of staff who has performed a study —
          and clicking a row opens that employee's dose history.
        </Bullet>
        <Bullet>
          Staff can view their own dose history; viewing someone else's
          requires the Personnel, Manager, or Supervisor team, and the
          organisation-wide overview requires Personnel.
        </Bullet>
      </Bullets>
    </Section>
  </Page>
)

export default ImagingRequests
